import React, { ReactNode } from "react";
import { RiLinkUnlinkM } from "@remixicon/react";
import {
    FieldTypedValue, FormField,
    FormFieldType, GetWorkflowServiceDocument, GetWorkflowServicesDocument, Integration, IntegrationActionType, useGetWorkflowServiceQuery,
    useSaveIntegrationSettingsMutation,
    useUninstallWorkflowServiceMutation,
    WorkflowFormField,
    WorkflowFormFieldSchema,
    WorkflowServiceOption,
    WorkflowStepKeyValueParameterInput,
} from "../../../../__generated__/api";
import FieldTypeValueDisplay from "../../../../components/form-field/FieldTypeValueDisplay";
import { HtmlRenderer } from "../../../../components/html-render/HtmlRenderer";
import FormGroup from "../../../../components/theme/form-group/FormGroup";
import SettingsContainer from "../../../../components/theme/settings";
import Toggle from "../../../../components/theme/toggle/Toggle";
import { useSetTitle } from "../base";

import { cloneWithoutTypename } from "../../../../api";
import { useFormBuilder } from "../../../../components/dirty-tracker/FormTracker";
import { isRecord } from "../../../../components/editable-text/JsonBuilder";
import UserGroups, { EVERYONE_GROUP_ID } from "../../../../components/pickers/user-groups/UserGroups";
import Popover from "../../../../components/popover/Popover";
import createTableContainer from "../../../../components/theme/table";
import { ContainsId } from "../../../../components/theme/table/base";
import useDeleteConfirm from "../../../../components/confirm/Delete";
import Button from "../../../../components/theme/button/Button";
import { LoggingHandler } from "../../../../contexts/logging/LoggingContext";
import useRouting from "../../../../contexts/Routing";

interface WorkflowStepKeyValueParameterInputWithId extends WorkflowStepKeyValueParameterInput, ContainsId {
    field: WorkflowFormField
}
const TableContainer = createTableContainer<WorkflowStepKeyValueParameterInputWithId>();

function copySetting(value: FieldTypedValue): FieldTypedValue {
    const copy = { ...value };
    delete copy.__typename;
    return copy;
}

const PERMISSION_GROUPS: Record<string, { text: string; description: string }> = {
    [IntegrationActionType.Execute]: {
        text: "Execute actions",
        description: "This integration allows users to execute certain actions from the ticket screen. "
            + "This controls which users are able to access this integration.",
    },
    [IntegrationActionType.UserDataProvider]: {
        text: "View User data",
        description: "This integration shows information about the user in the left hand ticket side. "
            + "This setting controls which users have access to view user information through this integration.",
    },
};

function SettingInput({
    field,
    value,
    optionValues,
    integration,
    onChange,
    option,
}: {
    field: FormField;
    value: FieldTypedValue;
    optionValues: FieldTypedValue[];
    integration: Integration;
    option: WorkflowServiceOption;
    onChange: (value: FieldTypedValue) => void;
}) {
    let control: ReactNode;
    let isFullWidth = false;

    const description = option.instructions || field.description;

    if (field.type === FormFieldType.Boolean) {
        control = (
            <Toggle
                // required={field.isRequired as boolean}
                checked={value?.boolean ?? false}
                onChange={event => onChange?.({ ...value, type: FormFieldType.Boolean, boolean: event.target.checked })}
            />
        );
    } else if (field.type === FormFieldType.Json) {
        const schema = (field as WorkflowFormField).schema as WorkflowFormFieldSchema;
        isFullWidth = true;
        const jsonValue = value?.string ? JSON.parse(value.string) as WorkflowStepKeyValueParameterInput[] : undefined;
        const fieldMap = isRecord(schema.root) ? schema.root.fields.toIdMap("id") : {};
        const areWorkflows = Object.values(fieldMap).some(row => row.type === FormFieldType.Workflow
            || row.type === FormFieldType.WorkflowTicket);
        const valueList = jsonValue?.mapFilter(row => {
            const fieldValue = fieldMap[row.key];
            return fieldValue ? {
                ...row,
                id: row.key,
                field: fieldValue,
            } : undefined;
        }) ?? [];
        //
        control = (
            <TableContainer data={valueList ?? []}>
                <TableContainer.TableControls>
                    <TableContainer.TableControls.Search
                        autoFocus={false}
                        search={(query, item) => item.id?.toLocaleString().includes(query)}
                    />
                    <TableContainer.TableControls.Seperator />
                    <TableContainer.TableControls.Filters shouldWriteUrl={false} />
                    <TableContainer.TableControls.SortBy />
                    {/* <TableContainer.TableControls.Create
                        text={`Add ${schema?.label}`}
                        onClick={() => {
                            //
                        }}
                    /> */}
                </TableContainer.TableControls>
                <TableContainer.Table>

                    <TableContainer.TableValue
                        columnKey="name"
                        title="Name"
                        sortBy={item => item.field.name}
                    >
                        {item => item.field.name as string}
                    </TableContainer.TableValue>
                    <TableContainer.TableValue
                        columnKey="description"
                        title="Description"
                        sortBy={item => item.field.description}
                    >
                        {item => (item.field.description
                            ? <HtmlRenderer html={item.field.description} shouldTrimBottom />
                            : "")}
                    </TableContainer.TableValue>

                    {areWorkflows && (
                        <TableContainer.TableValue
                            columnKey="workflow"
                            title="Workflow"
                        >
                            {item => (
                                <Popover
                                    text="Edit Workflow"
                                    variant="tooltip"
                                    iconVariant="secondary"
                                >
                                    Automations are disabled in the current version
                                </Popover>
                                // <Button.Edit
                                //     disabled
                                //     aria-disabled
                                //     title="Automations are disabled in the current version"
                                //     variant="ghost-blue"
                                //     size="small"
                                // >
                                //     Edit Workflow
                                // </Button.Edit>
                            )}
                        </TableContainer.TableValue>
                    )}

                </TableContainer.Table>
            </TableContainer>
        );
    } else if (field.type === FormFieldType.LongText) {
        control = (
            <FormGroup
                type="text"
                display="textarea"
                size="small"
                isRequired={field.isRequired as boolean}
                value={value?.string ?? ""}
                onChange={event => onChange?.({ ...value, type: FormFieldType.LongText, string: event.target.value })}
            />
        );
    }

    if (!control) {
        control = (
            <FieldTypeValueDisplay
                className={field.type === FormFieldType.Dropdown || field.type === FormFieldType.AsyncDropdown
                    ? SettingsContainer.Setting.PickerClassName
                    : undefined}
                inputClassName="form-control"
                shouldShowFieldName={false}
                shouldShowDescription={false}
                field={{
                    ...field,
                    isRequired: false,
                }}
                value={value}
                integrationId={integration?.id}
                getCurrentForm={() => optionValues}
                onChange={onChange}
            />
        );
    }

    return (
        <SettingsContainer.Setting
            title={field.name ?? ""}
            description={description ?? ""}
            descriptionElement={description ? <HtmlRenderer html={description} /> : undefined}
            permissions={SettingsContainer.Setting.Permissions.Administrator}
            isHideTitle={isFullWidth}
        >
            {control}
        </SettingsContainer.Setting>
    );
}

export default function ViewServiceSettings({
    id,
    childId,
}: {
    id: string;
    childId: string
}) {
    const { data, loading: isLoading } = useGetWorkflowServiceQuery({
        variables: {
            id,
        },
    });
    const integration = data?.workflowService?.installedIntegrations?.find(current => current.id === childId);

    const { historyPush, Paths } = useRouting();

    const { properties, tracker } = useFormBuilder(
        builder => builder
            .property("label", () => integration?.label ?? "")
            .array("options", () => integration?.settings?.map(copySetting) ?? [])
            .array("permissions", () => integration?.actionPermissions),
        [integration?.settings],
    );
    const [save] = useSaveIntegrationSettingsMutation();
    const [uninstall] = useUninstallWorkflowServiceMutation();

    const { element: saveElement } = tracker.useDirtyTracker({
        finishedSavingMessage: `Saved ${integration?.label} settings.`,
        onSave: async (__, onDone) => {
            await save({
                refetchQueries: [
                    { query: GetWorkflowServiceDocument, variables: { id } },
                ],
                variables: {
                    settings: {
                        id: integration?.id,
                        settings: cloneWithoutTypename(properties.options.value),
                        label: properties.label.value,
                        actionPermissions: properties.permissions.value
                            ? cloneWithoutTypename(properties.permissions.value)
                            : undefined,
                    },
                },
            });
            onDone();
        },
    });

    const { element: deleteElement, setDeleting } = useDeleteConfirm<Integration>(
        () => {
            LoggingHandler.instance.wrap(async () => {
                if (!integration) {
                    return;
                }
                await uninstall({
                    refetchQueries: [
                        { query: GetWorkflowServiceDocument, variables: { id } },
                        { query: GetWorkflowServicesDocument },
                    ],
                    variables: {
                        integrationId: integration.id,
                    },
                });

                historyPush(Paths.ADMINISTRATOR_SETTING, { setting: "workflow-services" });
            });
        },
        value => value.label,
        undefined,
        "Are you sure you want to uninstall",
        "Uninstall",

    );

    useSetTitle(integration?.label, [integration]);

    const valuesMap = properties.options.value?.toIdMap("fieldId") ?? {};

    return (
        <SettingsContainer saveElement={saveElement}>
            {deleteElement}

            <SettingsContainer.Section
                title="General Info"
            >
                <SettingsContainer.Setting
                    title="Connection Name"
                    description="Set up custom connection name."
                    permissions={SettingsContainer.Setting.Permissions.Administrator}
                    isLoading={isLoading}
                >
                    <FormGroup
                        type="text"
                        size="small"
                        value={properties.label.value}
                        onChange={properties.label.setValueFromInput}
                    />

                </SettingsContainer.Setting>
                <SettingsContainer.Setting
                    title="Linked Account"
                    description="Integration account that is linked to Foqal."
                    permissions={SettingsContainer.Setting.Permissions.Administrator}
                    isLoading={isLoading}
                >
                    <FormGroup
                        size="small"
                        type="text"
                        isDisabled
                        value={integration?.connectionUrl ? new URL(integration?.connectionUrl).hostname : ""}
                    />

                </SettingsContainer.Setting>
            </SettingsContainer.Section>

            {integration && data?.workflowService?.options.map(section => (
                <SettingsContainer.Section
                    title={section.title}
                    helpText={section.description ?? ""}
                >

                    {section.options.map(option => {
                        const field = option.input as FormField;
                        const value = valuesMap[field?.id] ?? field?.defaultValue;
                        if (!field) {
                            return null;
                        }
                        return (

                            <SettingInput
                                field={field}
                                value={value}
                                key={field.id}
                                option={option as WorkflowServiceOption}
                                optionValues={properties.options.value}
                                integration={integration as Integration}
                                onChange={changedValue => {
                                    const copy = [...properties.options.value];
                                    const index = properties.options.value.findIndex(row => row.fieldId === field.id);
                                    if (index >= 0) {
                                        copy[index] = changedValue;
                                    } else {
                                        copy.push({
                                            ...changedValue,
                                            fieldId: field.id as string,
                                            type: field.type as FormFieldType,
                                        });
                                    }

                                    properties.options.setValue(copy);
                                }}
                            />

                        );
                    })}
                </SettingsContainer.Section>
            ))}

            <SettingsContainer.Section
                title="Permissions"
            >
                {data?.workflowService?.actionTypes?.sortBy(row => row).map(type => {
                    if (!PERMISSION_GROUPS[type]) {
                        return null;
                    }
                    const { text, description } = PERMISSION_GROUPS[type];
                    const permission = properties.permissions.value?.find(group => group.action === type);
                    return (
                        <SettingsContainer.Setting
                            title={text}
                            key={type}
                            description={description}
                            permissions={SettingsContainer.Setting.Permissions.Administrator}
                        >

                            <UserGroups
                                isMulti
                                isClearable
                                canSelectEveryone
                                canSelectNone
                                className={SettingsContainer.Setting.PickerClassName}
                                value={permission?.groupIds ?? [EVERYONE_GROUP_ID]}
                                onSelected={groupIds => {
                                    const copy = [
                                        ...(properties.permissions.value ?? []),
                                    ];
                                    const currentIndex = copy?.findIndex(group => group.action === type);
                                    if (currentIndex >= 0) {
                                        copy[currentIndex] = {
                                            ...copy[currentIndex],
                                            groupIds: groupIds as string[],
                                        };
                                    } else {
                                        copy.push({
                                            action: type,
                                            groupIds: groupIds as string[],
                                        });
                                    }
                                    properties.permissions.setValue(copy);
                                }}

                            />

                        </SettingsContainer.Setting>
                    );
                })}
            </SettingsContainer.Section>
            <SettingsContainer.Section
                title="Danger Zone"
            >
                <SettingsContainer.Setting
                    title="Uninstall Connection"
                    description="This will deactivate the connection from interacting with this space."
                    permissions={SettingsContainer.Setting.Permissions.Administrator}
                >

                    <Button variant="outline-danger" onClick={() => setDeleting(integration)} prefixIcon={<RiLinkUnlinkM />}>
                        Uninstall
                    </Button>

                </SettingsContainer.Setting>
            </SettingsContainer.Section>

        </SettingsContainer>
    );
}
